import { toast } from "react-toastify";

import { handleError } from "@app/util/handle-error";
import {
  GetBannersExtendedStatusEnum,
  BannersApi,
  CreateBannerDTO,
  PutBannerDTO,
  OutBannerDTO,
  GetBannersExtendedBannerTypeEnum,
  BANNERTYPEENUM
} from "@app/api/generated";
import { getAuthorizedApiConfig } from "@app/util/api-config";
import { AppThunk } from "@app/redux/store";
import {
  deleteBannerFromList,
  setSelectedBanner,
  setBanners,
  setIsLoadingBanners,
  setTotalResults
} from "@app/redux/reducers/banners";

const getBannerStatus = (value?: number): GetBannersExtendedStatusEnum | undefined => {
  switch (value) {
    case 0:
      return undefined;
    case 1:
      return GetBannersExtendedStatusEnum.INVISIBLE;
    case 2:
      return GetBannersExtendedStatusEnum.VISIBLE;
    case 3:
      return GetBannersExtendedStatusEnum.ALMOST_VISIBLE;
    default:
      return undefined;
  }
};

type GetBannersProps = {
  skip: number;
  take: number;
  status?: number;
  searchQuery?: string;
  platformId?: number;
  type?: GetBannersExtendedBannerTypeEnum;
};

const getBanners =
  ({ skip, take, status, searchQuery, platformId, type }: GetBannersProps): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      dispatch(setIsLoadingBanners(true));

      const api = new BannersApi(await getAuthorizedApiConfig());

      const bannerStatus = getBannerStatus(status);

      const result = await api.getBannersExtended({
        take,
        skip,
        bannerType: type || undefined,
        status: bannerStatus,
        searchQuery,
        platformId
      });

      dispatch(setBanners(result.banners));
      dispatch(setTotalResults(result.total));
      dispatch(setIsLoadingBanners(false));
    } catch (error) {
      handleError(error, dispatch, "Failed to get banners");
    }
  };

const getBanner =
  (id: number): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      dispatch(setIsLoadingBanners(true));
      const api = new BannersApi(await getAuthorizedApiConfig());

      const result: OutBannerDTO = await api.getBannerById({ bannerId: id });

      dispatch(setSelectedBanner(result));
      dispatch(setIsLoadingBanners(false));
    } catch (error) {
      handleError(error, dispatch, "Failed to get banner");
    }
  };

const deleteBanner =
  (id: number): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const api = new BannersApi(await getAuthorizedApiConfig());

      const bannerDeleted = await api.deleteBanner({ bannerId: id });

      if (bannerDeleted) {
        dispatch(deleteBannerFromList(id));
      }
    } catch (error) {
      handleError(error, dispatch, "Failed to delete banner");
    }
  };

const createNewBanner =
  (platformId: number, name: string, type?: BANNERTYPEENUM): AppThunk =>
  async (dispatch): Promise<OutBannerDTO | undefined> => {
    try {
      const createBannerDTO: CreateBannerDTO = {
        platform: { id: platformId },
        name,
        type
      };

      const api = new BannersApi(await getAuthorizedApiConfig());

      const createdBanner: OutBannerDTO = await api.postBanner({ createBannerDTO });

      dispatch(setSelectedBanner(createdBanner));

      return createdBanner;
    } catch (error) {
      if ((error as any)?.status === 422) {
        handleError(error, dispatch, "Banner bestaat al.");
      } else {
        handleError(error, dispatch, "Failed to create new banner.");
      }
    }

    return undefined;
  };

const saveBanner =
  (banner: PutBannerDTO, customMessage?: string): AppThunk =>
  async (dispatch): Promise<void> => {
    try {
      const api = new BannersApi(await getAuthorizedApiConfig());

      const savedBanner = await api.putBanner({ bannerId: banner.id, putBannerDTO: banner });

      dispatch(setSelectedBanner(savedBanner));

      if (customMessage) {
        toast.success(customMessage);
      } else {
        toast.success("Banner is opgeslagen");
      }
    } catch (error) {
      handleError(error, dispatch, "Failed to save banner");
    }
  };

export const bannersThunks = {
  getBanners,
  getBanner,
  deleteBanner,
  createNewBanner,
  saveBanner
};
